// PR-29: native keyboard show/hide listeners.
// PR-38: also hide the iOS keyboard accessory bar on Capacitor startup.
//
// `MobileCapture` uses these to scroll the input into view + adjust the
// bottom-nav offset when the on-screen keyboard appears. On the web
// build (WeChat H5 / PWA — the doc/12 launch surface) the same signal
// comes from the visualViewport API instead of the Capacitor plugin.
import { Keyboard, KeyboardStyle } from '@capacitor/keyboard';
import type { PluginListenerHandle } from '@capacitor/core';
import { isCapacitor } from './platform';

// Below this many px of occlusion the resize is treated as browser
// chrome twitching (URL bar collapse, etc), not a keyboard.
const WEB_KEYBOARD_MIN = 90;

// Soft-keyboard detection for the plain-browser build. When the keyboard
// opens, the visual viewport shrinks while the layout viewport stays put
// (iOS never resizes layout for the keyboard; Chrome keeps it with the
// interactive-widget=overlays-content meta mobile.html sets) — so the
// occluded band at the bottom is the keyboard height. offsetTop folds in
// any pan the browser applied to keep the focused input visible.
function listenToWebKeyboard(
  onShow: (height: number) => void,
  onHide: () => void,
): () => void {
  const vv = window.visualViewport;
  if (!vv) return () => {}; // very old WebView — keyboard state stays closed
  let open = false;
  const onChange = () => {
    const occluded = window.innerHeight - vv.height - vv.offsetTop;
    if (occluded > WEB_KEYBOARD_MIN) {
      open = true;
      onShow(occluded);
    } else if (open) {
      open = false;
      onHide();
    }
  };
  vv.addEventListener('resize', onChange);
  vv.addEventListener('scroll', onChange);
  return () => {
    vv.removeEventListener('resize', onChange);
    vv.removeEventListener('scroll', onChange);
  };
}

export function listenToKeyboard(
  onShow: (height: number) => void,
  onHide: () => void,
): () => void {
  if (!isCapacitor()) return listenToWebKeyboard(onShow, onHide);
  let showHandle: PluginListenerHandle | undefined;
  let hideHandle: PluginListenerHandle | undefined;
  Keyboard.addListener('keyboardWillShow', (info) => onShow(info.keyboardHeight)).then((h) => {
    showHandle = h;
  });
  Keyboard.addListener('keyboardWillHide', () => onHide()).then((h) => {
    hideHandle = h;
  });
  return () => {
    showHandle?.remove();
    hideHandle?.remove();
  };
}

// PR-38: hide the iOS UIToolbar that WKWebView attaches above the keyboard
// for any focused input/textarea/contenteditable (the `< > Done` bar). It
// can't be removed from web code in a Capacitor wrap — only via this
// native API. No-op in browser. Safe to call once at boot.
export function disableNativeAccessoryBar(): void {
  if (!isCapacitor()) return;
  Keyboard.setAccessoryBarVisible({ isVisible: false }).catch((err) => {
    console.warn('setAccessoryBarVisible failed:', err);
  });
}

// Force the soft keyboard to its light appearance. Without this it
// follows the system and lands dark when the device is in dark mode —
// a mismatch against the always-light warm-paper UI. No-op in browser.
export function setKeyboardLightStyle(): void {
  if (!isCapacitor()) return;
  Keyboard.setStyle({ style: KeyboardStyle.Light }).catch((err) => {
    console.warn('Keyboard.setStyle failed:', err);
  });
}
